import type { PaymentProviderName } from "@/lib/config";
import type { PaymentSession } from "./types";

const STORAGE_KEY = "portal-pending-payment";

/**
 * Minimal session reference persisted between phase 1 (checkout start) and
 * phase 2 (return), so the return handler knows which session/provider to use
 * even when the provider's return URL does not carry our sessionId.
 */
export interface PendingPaymentSession {
  paymentSessionId: string;
  providerSessionId?: string;
  provider: PaymentProviderName;
  orderReference?: string;
}

export function storePendingSession(pending: PendingPaymentSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
}

export function readPendingSession(): PendingPaymentSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingPaymentSession;
  } catch {
    return null;
  }
}

export function clearPendingSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

const CHECKOUT_SESSION_KEY = "portal-checkout-session";

export function storeCheckoutSession(session: PaymentSession): void {
  localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(session));
}

export function readCheckoutSession(providerSessionId: string): PaymentSession | null {
  const raw = localStorage.getItem(CHECKOUT_SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as PaymentSession;
    if (session.sessionId !== providerSessionId) return null;
    if (session.expiresAt && new Date(session.expiresAt) < new Date()) return null;
    return session;
  } catch {
    return null;
  }
}
